import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert,
  Snackbar,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SearchIcon from '@mui/icons-material/Search';

function DepositAdmin() {
  const [tabValue, setTabValue] = useState(0);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [action, setAction] = useState('add'); // 'add' or 'pay'

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchDepositOverview();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      filterUsers();
    }
  }, [searchTerm, users]);

  const fetchDepositOverview = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/deposit/admin-overview');
      setOverview(response.data);
      setLoading(false);
    } catch (err) {
      showNotification('Failed to load deposit overview', 'error');
      setLoading(false);
      console.error('Deposit overview fetch error:', err);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      // Change to fetch app_users first instead of admin users
      const response = await axios.get('/app-users');
      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
      setUsersLoading(false);
    } catch (err) {
      // If app_users fails, try admin users as fallback
      try {
        console.log('Could not fetch app users, trying admin users as fallback');
        const adminResponse = await axios.get('/admin/users');
        setUsers(adminResponse.data.users);
        setFilteredUsers(adminResponse.data.users);
      } catch (adminErr) {
        console.error('Failed to fetch any users:', adminErr);
        showNotification('Failed to load users', 'error');
      } finally {
        setUsersLoading(false);
      }
    }
  };

  const filterUsers = () => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      (user.phone && user.phone.includes(term))
    );
    
    setFilteredUsers(filtered);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    fetchDepositOverview();
    fetchUsers();
  };

  const handleOpenDialog = (user, actionType) => {
    setSelectedUser(user);
    setAction(actionType);
    setAmount('');
    setNote('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  const handleSubmitAction = async () => {
    if (!selectedUser || !amount) return;
    
    try {
      let response;
      
      if (action === 'add') {
        // Add deposit
        response = await axios.post('/deposit/add', {
          userId: selectedUser.id,
          amount: parseFloat(amount),
          notes: note || `Manual deposit added by admin`
        });
        
        showNotification(`Deposit of ¥${amount} added successfully for ${selectedUser.username}`, 'success');
      } else {
        // Process credit payment
        response = await axios.post('/deposit/pay-credit', {
          userId: selectedUser.id,
          amount: parseFloat(amount),
          paymentMethod: paymentMethod,
          notes: note || `Credit payment processed by admin`
        });
        
        showNotification(`Credit payment of ¥${amount} processed for ${selectedUser.username}`, 'success');
      }
      
      handleCloseDialog();
      fetchDepositOverview();
      fetchUsers();
    } catch (err) {
      console.error('Error processing action:', err);
      showNotification(err.response?.data?.message || 'Failed to process the transaction', 'error');
    }
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `¥${parseFloat(amount || 0).toFixed(0)}`;
  };

  if (loading && !overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Deposit Management</Typography>
        <Button 
          startIcon={<RefreshIcon />} 
          variant="outlined" 
          onClick={handleRefresh}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Deposits</Typography>
              <Typography variant="h3" color="primary">
                {formatCurrency(overview?.stats?.total_deposits)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total deposits across all users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Credit Used</Typography>
              <Typography variant="h3" color="error">
                {formatCurrency(overview?.stats?.total_credit_used)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unpaid credit across all users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Net Balance</Typography>
              <Typography variant="h3">
                {formatCurrency((overview?.stats?.total_deposits || 0) - (overview?.stats?.total_credit_used || 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total deposits minus credit used
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Recent Transactions" />
        <Tab label="Users with Credit" />
        <Tab label="All Users" />
      </Tabs>

      {/* Recent Transactions Tab */}
      {tabValue === 0 && (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table aria-label="recent transactions table">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview?.recentTransactions?.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{formatDate(transaction.created_at)}</TableCell>
                    <TableCell>{transaction.username}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.transaction_type.replace('_', ' ')}
                        color={
                          transaction.transaction_type === 'deposit' ? 'success' :
                          transaction.transaction_type === 'withdrawal' ? 'warning' :
                          transaction.transaction_type === 'call_charge' ? 'primary' :
                          transaction.transaction_type === 'credit' ? 'error' :
                          'default'
                        }
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.transaction_type === 'deposit' || transaction.transaction_type === 'refund' 
                        ? `+${formatCurrency(transaction.amount)}` 
                        : `-${formatCurrency(transaction.amount)}`}
                    </TableCell>
                    <TableCell>{formatCurrency(transaction.balance)}</TableCell>
                    <TableCell>{transaction.reference_id || '-'}</TableCell>
                    <TableCell>{transaction.notes}</TableCell>
                  </TableRow>
                ))}
                {!overview?.recentTransactions?.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No recent transactions</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Users with Credit Tab */}
      {tabValue === 1 && (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table aria-label="users with credit table">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Deposit Balance</TableCell>
                  <TableCell>Credit Used</TableCell>
                  <TableCell>Net Balance</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview?.topCreditUsers?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{formatCurrency(user.deposit_balance)}</TableCell>
                    <TableCell>{formatCurrency(user.credit_used)}</TableCell>
                    <TableCell>{formatCurrency(user.deposit_balance - user.credit_used)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenDialog(user, 'add')}
                      >
                        Add Deposit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<MoneyOffIcon />}
                        onClick={() => handleOpenDialog(user, 'pay')}
                        disabled={parseFloat(user.credit_used) <= 0}
                      >
                        Pay Credit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!overview?.topCreditUsers?.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No users with credit</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* All Users Tab */}
      {tabValue === 2 && (
        <>
          <Box mb={2}>
            <TextField
              fullWidth
              placeholder="Search by username, email, or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer>
              <Table aria-label="all users table">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Deposit Balance</TableCell>
                    <TableCell>Credit Used</TableCell>
                    <TableCell>Net Balance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>{formatCurrency(user.deposit_balance || 0)}</TableCell>
                        <TableCell>{formatCurrency(user.credit_used || 0)}</TableCell>
                        <TableCell>
                          {formatCurrency((user.deposit_balance || 0) - (user.credit_used || 0))}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            sx={{ mr: 1 }}
                            onClick={() => handleOpenDialog(user, 'add')}
                          >
                            Add Deposit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<MoneyOffIcon />}
                            onClick={() => handleOpenDialog(user, 'pay')}
                            disabled={!user.credit_used || parseFloat(user.credit_used) <= 0}
                          >
                            Pay Credit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {!usersLoading && filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No users found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Add Deposit or Pay Credit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {action === 'add' ? 'Add Deposit' : 'Process Credit Payment'} for {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Amount"
                fullWidth
                type="text"
                value={amount}
                onChange={handleAmountChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                }}
              />
            </Grid>
            
            {action === 'pay' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                label="Note"
                fullWidth
                multiline
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Grid>
            
            {action === 'pay' && selectedUser && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Current credit used: {formatCurrency(selectedUser.credit_used)}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitAction} 
            color="primary" 
            variant="contained"
            disabled={!amount || parseFloat(amount) <= 0}
          >
            {action === 'add' ? 'Add Deposit' : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DepositAdmin;